/**
 * Profile Picture Server Functions
 *
 * Run on the server, so the upload/delete calls must forward the
 * browser's cookies + CSRF token — server-side fetch has no cookie jar and
 * `profilePictureApi` relies on `credentials: 'include'` + browser-side
 * `getCsrfToken()`, which are both empty here (every call would 401/403).
 */

import { createServerFn } from '@tanstack/react-start';
import { logger } from '@/lib/utils/logger';
import { compressWithBunImage } from '@/lib/server-utils/bun-image-compressor';
import { authenticatedServerRequest, ServerRequestError } from '@/lib/server-fns/ssr-fetch';
import type { UploadResponse } from '@/lib/api/v2/profile-picture';
import type { ApiError } from '@/lib/types/errors';
import { err, ok, type Result } from '@/lib/types/result';

/**
 * Core upload logic, extracted so it is unit-testable without the TanStack
 * Start server runtime (createServerFn handlers can't be invoked directly
 * in a test — they need the request AsyncLocalStorage context).
 *
 * The `upload` callable is injected so tests can stub it and the production
 * server-fn can pass a cookie-forwarding implementation.
 */
export async function processProfilePictureUpload(
  file: File,
  upload: (f: File) => Promise<Result<UploadResponse, ApiError>>
) {
  logger.info('uploadProfilePictureServer', 'Compressing with Bun.Image');
  const compressionResult = await compressWithBunImage(file);

  if (compressionResult.success) {
    logger.info('uploadProfilePictureServer', 'Bun.Image compression successful', {
      method: compressionResult.method,
      originalSize: compressionResult.originalSize,
      compressedSize: compressionResult.compressedSize,
      compressionRatio: compressionResult.compressionRatio,
    });
  } else {
    logger.warn('uploadProfilePictureServer', 'Bun.Image compression failed, using original', {
      method: compressionResult.method,
      error: compressionResult.error,
    });
  }

  const result = await upload(compressionResult.file);

  if (!result.ok) {
    logger.error('uploadProfilePictureServer', result.error);
    throw new Error(result.error.message || 'Failed to upload profile picture');
  }

  logger.info('uploadProfilePictureServer', 'Profile picture uploaded successfully');
  return result.value;
}

/**
 * Resolve + validate the file from a FormData payload, returning it as a File
 * or throwing a descriptive error. Extracted for direct unit testing.
 */
export function extractProfilePictureFile(data: FormData): File {
  const file = data.get('file');
  if (!file || !(file instanceof File)) {
    throw new Error('No file provided');
  }
  return file;
}

/**
 * Cookie-forwarding multipart upload used by the server function. Rebuilds a
 * FormData from the (possibly recompressed) file and POSTs it with the
 * incoming request's cookies + CSRF, since profilePictureApi.uploadPicture's
 * `credentials: 'include'` / browser-side CSRF are empty on the server.
 */
async function uploadPictureForwarded(file: File): Promise<Result<UploadResponse, ApiError>> {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const value = await authenticatedServerRequest<UploadResponse>('/v2/me/profile/picture', {
      method: 'POST',
      body: formData,
      contentType: false,
    });
    return ok(value);
  } catch (error) {
    if (error instanceof ServerRequestError) return err(error.apiError);
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to upload picture',
    });
  }
}

/**
 * Upload Profile Picture (Server)
 * Input is FormData containing the file.
 */
export const uploadProfilePictureServer = createServerFn({ method: 'POST' })
  .validator((data: FormData) => data)
  // Handler must stay async — TanStack Start requires server-fn handlers to be
  // async. It has no direct await because it delegates to processProfilePictureUpload.
  .handler(async ({ data }) => {
    'use server';
    logger.info('uploadProfilePictureServer', 'Uploading profile picture');

    const file = extractProfilePictureFile(data);
    return processProfilePictureUpload(file, uploadPictureForwarded);
  });
